import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Music, 
  Cloud, 
  Smartphone, 
  Settings, 
  Headphones, 
  Radio,
  Cpu,
  Layers,
  Building2,
  Palette,
  ExternalLink,
  Zap,
  BarChart3
} from 'lucide-react';
import { d1AudioSamples, images } from '../data/mock';

const ProductsSection = () => {
  const [activeDemo, setActiveDemo] = useState('before');
  
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const mediaProducts = [
    {
      name: 'Acoustic Intelligence',
      category: 'AIQ Technology Platform',
      icon: Zap,
      description: 'Pioneering field merging sound science with technological innovation to transform how individuals experience and interact with digital and physical environments. Bridges human perception and machine learning through MSML principles.',
      features: [
        'AudioDNA for content recognition & fingerprinting',
        'Temporal synchrony for immersive experiences',
        'LDE content protection & authentication',
        'Multi-sensory data synchronization',
        'AI-driven audio analysis & optimization'
      ],
      status: 'Production Ready',
      color: 'from-cyan-500 to-blue-600',
      website: 'https://pulsbeat.ca/products',
      screenshot: 'https://images.unsplash.com/photo-1709846486283-de18cb67bc67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxhY291c3RpYyUyMGludGVsbGlnZW5jZXxlbnwwfHx8Ymx1ZXwxNzYyMjU5NjA3fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      name: 'D1 AUDIO',
      category: 'Novel Audio Codec and Programmable media container',
      icon: Headphones,
      description: 'Programmable Media Container with fidelity lossless codec for 3D Hi-Res audio and advanced metadata integration.',
      features: [
        'STEMS for creators and producers',
        'Live Lyrics & Chord synchronization',
        'Data Steganography for content protection',
        'Cross-platform compatibility',
        '21x data volume reduction'
      ],
      status: 'Production Ready',
      color: 'from-blue-500 to-cyan-600',
      website: 'https://d1audio.com',
      screenshot: 'https://images.unsplash.com/photo-1698429894931-fa699270a2fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxhdWRpbyUyMHdhdmVmb3JtfGVufDB8fHxibHVlfDE3NjIyNTk2MTF8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      name: 'AudioMine',
      category: 'Cloud Platform',
      icon: Cloud,
      description: 'AI-powered cloud infrastructure platform with Attention Reward RPM Tokens and Intelligent Live Link Assistant.',
      features: [
        'Content Module with smart cataloging',
        'AudioDNA Module for audio fingerprinting',
        'ArtAssets Module for multimedia management',
        'Revenue optimization algorithms',
        'Real-time analytics dashboard'
      ],
      status: 'Active Development',
      color: 'from-green-500 to-emerald-600',
      website: 'https://www.audiomineapp.com',
      screenshot: 'https://customer-assets.emergentagent.com/job_mode-aware-site/artifacts/au4ckflm_Screenshot%202025-11-04%20at%207.39.34%E2%80%AFAM.png'
    },
    {
      name: 'cDM Cloud',
      category: 'Infrastructure Platform',
      icon: Building2,
      description: 'Cognitive Dynamic Media Cloud with AI-driven media solutions and global distribution networks.',
      features: [
        'Scalable server infrastructure',
        'Intelligent audio optimization',
        'Secure transmission protocols',
        'Edge computing capabilities',
        'API-first architecture'
      ],
      status: 'Beta Testing',
      color: 'from-purple-500 to-pink-600',
      website: 'https://www.cdmcloud.ai',
      screenshot: 'https://images.pexels.com/photos/5475810/pexels-photo-5475810.jpeg'
    },
    {
      name: 'my.ARTIST.one',
      category: 'Artist Platform',
      icon: Palette,
      description: 'Artist Branded App Ecosystem with D9NAMIC media player and LIVELINK synchronization technology.',
      features: [
        'Personalized artist applications',
        'Advanced fan engagement tools',
        'Real-time content synchronization',
        'Multi-platform distribution',
        'Monetization integrations'
      ],
      status: 'Pilot Programs',
      color: 'from-orange-500 to-red-600',
      website: 'https://myartist.one',
      screenshot: 'https://images.unsplash.com/photo-1504376626428-15e04b55193b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhcnRpc3QlMjBtdXNpY3xlbnwwfHx8b3JhbmdlfDE3NjIyNTk2Mjd8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      name: 'Business Intelligence AI',
      category: 'Partner Solution for SAP Business Intelligence',
      icon: BarChart3,
      description: 'AI-powered conversational BI that transforms SAP BusinessObjects data into actionable insights. Natural language queries deliver instant answers with enterprise-grade security.',
      features: [
        'Auto Web Intelligence report generation',
        'Natural language queries in plain English',
        '10x faster time to insights (30 seconds)',
        '90% reduction in IT reporting requests',
        'Unlimited users with no per-seat costs'
      ],
      status: 'Production Ready',
      color: 'from-indigo-500 to-blue-600',
      website: 'https://www.trustedbisolutions.com/',
      screenshot: 'https://images.unsplash.com/photo-1650327034581-1711a15a5430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxkYXRhJTIwYW5hbHl0aWNzfGVufDB8fHxibHVlfDE3NjIyNjEyMzB8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  const iddoServices = [
    {
      title: 'Custom IDDO Implementation',
      description: 'Tailored data density optimization solutions designed for specific industry requirements and use cases.',
      deliverables: [
        'Requirements analysis & system design',
        'Custom MSML algorithm adaptation',
        'Integration with existing infrastructure',
        'Performance optimization & testing',
        'Documentation & training'
      ],
      icon: Settings
    },
    {
      title: 'Media Technology Consulting',
      description: 'Expert consultation on implementing advanced media technologies and optimization strategies.',
      deliverables: [
        'Technology assessment & roadmap',
        'Architecture design & planning',
        'Implementation strategy development',
        'Performance benchmarking',
        'Ongoing technical support'
      ],
      icon: Cpu
    },
    {
      title: 'Platform Development Services',
      description: 'End-to-end development of MSML-powered platforms for media, telecom, and autonomous systems.',
      deliverables: [
        'Full-stack platform development',
        'Cloud infrastructure setup',
        'API design & implementation',
        'Security & compliance integration',
        'Deployment & maintenance support'
      ],
      icon: Layers
    }
  ];

  return (
    <section id="products" className="py-20 bg-slate-800 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-20">
        <img
          src={images.dataCompression}
          alt="Data Technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/90 to-slate-900/90" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            MSML-Powered <span className="text-blue-400">Products</span> & Services
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive portfolio of media market products and specialized IDDO solution development services
          </p>
        </motion.div>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-900 border border-blue-400/20 mb-12">
            <TabsTrigger value="products" className="data-[state=active]:bg-blue-400/20 data-[state=active]:text-blue-400">
              Media Products
            </TabsTrigger>
            <TabsTrigger value="demo" className="data-[state=active]:bg-blue-400/20 data-[state=active]:text-blue-400">
              Technology Demo
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-blue-400/20 data-[state=active]:text-blue-400">
              IDDO Services
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {mediaProducts.map((product, index) => {
                  const IconComponent = product.icon;
                  return (
                    <motion.div
                      key={product.name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02, y: -5 }}
                      onClick={() => product.website && window.open(product.website, '_blank')}
                      className={product.website ? 'cursor-pointer' : ''}
                    >
                      <Card className="h-full bg-slate-900/50 border-blue-400/20 hover:border-blue-400/40 transition-all duration-300 overflow-hidden">
                        <CardHeader>
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${product.color} flex items-center justify-center`}>
                                <IconComponent className="text-white" size={24} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <CardTitle className="text-white text-xl">{product.name}</CardTitle>
                                  {product.website && (
                                    <ExternalLink className="text-blue-400" size={16} />
                                  )}
                                </div>
                                <CardDescription className="text-blue-400">{product.category}</CardDescription>
                              </div>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={`border-blue-400/30 text-blue-400 text-xs`}
                            >
                              {product.status}
                            </Badge>
                          </div>
                          <p className="text-gray-300 leading-relaxed mb-4">
                            {product.description}
                          </p>
                        </CardHeader>
                        <CardContent>
                          <div className="flex gap-4">
                            {/* Left side: Key Features */}
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-cyan-400 mb-3">Key Features:</h4>
                              <ul className="space-y-2">
                                {product.features.map((feature, i) => (
                                  <li key={i} className="flex items-start text-sm text-gray-300">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            {/* Right side: Website Screenshot Preview */}
                            {product.screenshot && (
                              <div className="w-64 flex-shrink-0">
                                <div className="relative rounded-lg overflow-hidden border border-blue-400/30 group">
                                  <img 
                                    src={product.screenshot} 
                                    alt={`${product.name} website preview`}
                                    className={`w-full h-48 object-cover ${product.name === 'my.ARTIST.one' ? 'object-[65%_35%]' : 'object-top'}`}
                                  />
                                  {product.website && (
                                    <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/10 transition-colors flex items-center justify-center">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-blue-500 rounded-full p-3">
                                        <ExternalLink className="text-white" size={20} />
                                      </div>
                                    </div>
                                  )}
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent p-2">
                                    <p className="text-xs text-gray-300 text-center">Website Preview</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {product.website && (
                            <div className="mt-4 pt-4 border-t border-blue-400/20">
                              <button className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-2 transition-colors">
                                <span>Visit {product.name} Website</span>
                                <ExternalLink size={14} />
                              </button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="demo" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="bg-slate-900/50 border-blue-400/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-white text-center">
                    D1 Audio IDDO Technology Demonstration
                  </CardTitle>
                  <p className="text-gray-300 text-center text-sm">
                    Real audio samples demonstrating 21x data volume reduction optimized for perceptual transparency
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" style={{ minHeight: '500px' }}>
                    {/* Spectrogram Display */}
                    <div className="flex flex-col justify-between space-y-4">
                      <div className="relative bg-slate-700 rounded-xl overflow-hidden" style={{ height: '400px' }}>
                        <img
                          src={activeDemo === 'before' ? d1AudioSamples.original.spectrogram : d1AudioSamples.compressed.spectrogram}
                          alt={activeDemo === 'before' ? 'Original Audio Spectrogram' : 'D1 Audio Compressed Spectrogram'}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4 bg-slate-900/80 rounded-lg px-3 py-2">
                          <span className="text-white font-semibold text-sm">
                            {activeDemo === 'before' ? 'Original 48kHz-16bit' : 'D1 Audio (MSML decoded)'}
                          </span>
                        </div>
                        
                        {/* Spectrogram Info */}
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-center justify-between bg-slate-900/80 rounded-lg p-3">
                            <div className="text-white text-sm">
                              <span className="font-medium">Frequency Analysis:</span> 
                              <span className="ml-2 text-cyan-400">
                                {activeDemo === 'before' ? '0-24 kHz' : '0-24 kHz (Preserved)'}
                              </span>
                            </div>
                            <Badge variant="outline" className={`${activeDemo === 'after' ? 'border-green-400 text-green-400' : 'border-blue-400 text-blue-400'}`}>
                              {activeDemo === 'before' ? 'Original Spectrum' : 'D1 Audio Spectrum'}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Toggle Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          variant={activeDemo === 'before' ? 'default' : 'outline'}
                          onClick={() => setActiveDemo('before')}
                          className={`flex-1 ${activeDemo === 'before' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-gray-600 text-gray-400 hover:bg-slate-700'}`}
                        >
                          <Music className="mr-2" size={16} />
                          Original (.wav)
                        </Button>
                        <Button
                          variant={activeDemo === 'after' ? 'default' : 'outline'}
                          onClick={() => setActiveDemo('after')}
                          className={`flex-1 ${activeDemo === 'after' ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white' : 'border-gray-600 text-gray-400 hover:bg-slate-700'}`}
                        >
                          <Radio className="mr-2" size={16} />
                          D1 Audio (.zsa)
                        </Button>
                      </div>
                    </div>

                    {/* D1 Audio Sample Specifications */}
                    <div className="flex flex-col justify-between space-y-4">
                      {/* Audio Specs Display */}
                      <div className="bg-slate-800/70 rounded-lg p-6 border border-blue-400/20" style={{ height: '400px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-blue-400 mb-1">
                              {activeDemo === 'before' ? d1AudioSamples.original.size : d1AudioSamples.compressed.size}
                            </div>
                            <div className="text-sm text-gray-400">File Size</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold text-cyan-400 mb-1">
                              {activeDemo === 'before' ? d1AudioSamples.original.bitrate : d1AudioSamples.compressed.bitrate}
                            </div>
                            <div className="text-sm text-gray-400">Bitrate</div>
                          </div>
                        </div>
                        
                        <div className="text-center mb-4">
                          <Badge variant="outline" className={`${activeDemo === 'after' ? 'border-green-400 text-green-400' : 'border-blue-400 text-blue-400'}`}>
                            {activeDemo === 'before' ? d1AudioSamples.original.format : d1AudioSamples.compressed.format} Format
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 text-sm text-center">
                          {activeDemo === 'before' ? d1AudioSamples.original.description : d1AudioSamples.compressed.description}
                        </p>

                        {/* Compression Achievement */}
                        {activeDemo === 'after' && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="p-4 bg-gradient-to-r from-green-900/30 to-blue-900/30 rounded-lg border border-green-400/30"
                          >
                            <div className="flex items-center justify-center mb-2">
                              <span className="text-green-400 font-semibold text-lg">{d1AudioSamples.compressionRatio}</span>
                            </div>
                            <p className="text-gray-300 text-sm text-center">
                              From 122MB to 5.8MB with perceptually transparent audio quality
                            </p>
                          </motion.div>
                        )}

                        {/* Download Information */}
                        <div className="bg-slate-700/30 rounded-lg p-4 border border-cyan-400/20">
                          <h4 className="text-cyan-400 font-semibold mb-2">Experience D1 Audio</h4>
                          <p className="text-gray-300 text-sm mb-3">
                            Download the demo player and audio samples to compare quality yourself.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button 
                              size="sm" 
                              className="bg-cyan-600 hover:bg-cyan-700 text-xs"
                              onClick={() => window.open('https://d1audio.com/evolving-1', '_blank')}
                            >
                              Download Demo Player
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 text-xs"
                              onClick={() => window.open('https://d1audio.com/evolving-1', '_blank')}
                            >
                              Get Audio Samples
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          <TabsContent value="services" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-4 text-center">
                  Innovative IDDO Solutions Development
                </h3>
                <p className="text-gray-300 text-center max-w-3xl mx-auto mb-8">
                  Specialized services for developing custom Intelligent Data Density Optimization solutions tailored to specific market niches and client requirements
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {iddoServices.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <motion.div
                      key={service.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      <Card className="h-full bg-slate-900/50 border-cyan-400/20 hover:border-cyan-400/40 transition-all duration-300">
                        <CardHeader>
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="w-12 h-12 rounded-lg bg-cyan-400/20 flex items-center justify-center">
                              <IconComponent className="text-cyan-400" size={24} />
                            </div>
                            <CardTitle className="text-white text-lg">{service.title}</CardTitle>
                          </div>
                          <CardDescription className="text-gray-300 leading-relaxed">
                            {service.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <h4 className="text-sm font-semibold text-cyan-400 mb-3">Deliverables:</h4>
                          <ul className="space-y-2">
                            {service.deliverables.map((deliverable, i) => (
                              <li key={i} className="flex items-start text-sm text-gray-300">
                                <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                {deliverable}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                viewport={{ once: true }}
                className="mt-12 text-center"
              >
                <div className="bg-slate-900/50 rounded-xl p-8 border border-cyan-400/20">
                  <h4 className="text-xl font-semibold text-white mb-4">Ready to Optimize Your Data?</h4>
                  <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                    Contact our team to discuss how MSML technology can be adapted for your specific use case and industry requirements.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                      onClick={() => scrollToSection('#contact')}
                    >
                      Request Consultation
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900"
                      onClick={() => scrollToSection('#contact')}
                    >
                      Schedule Demo
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ProductsSection;